import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AdvancedSection } from "@/modules/shell/AdvancedSection";
import { AppPicker } from "@/modules/shell/AppPicker";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import { listProducts, type ProductRow } from "@/shared/api/inventory";
import {
  createPurchaseOrder,
  createSupplier,
  listSuppliers,
} from "@/shared/api/purchasing";
import { DocNumberField } from "@/modules/core/components/DocNumberField";
import { TaxAdvancedFields } from "@/modules/core/components/TaxAdvancedFields";
import { getOrgSettings } from "@/shared/api/org-settings";

type LineDraft = { productId: string; qty: string; unitCost: string };

export function PurchaseOrderNewPage() {
  const { companySlug } = useParams();
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [orgTaxPriceMode, setOrgTaxPriceMode] = useState("inclusive");
  const [orgTaxRatePct, setOrgTaxRatePct] = useState("12");
  const [taxPriceMode, setTaxPriceMode] = useState<
    "" | "inclusive" | "exclusive"
  >("");
  const [taxRatePct, setTaxRatePct] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [newSupplierName, setNewSupplierName] = useState("");
  const [docNumber, setDocNumber] = useState("");
  const [supplierRef, setSupplierRef] = useState("");
  const [expectedOn, setExpectedOn] = useState("");
  const [paymentDueOn, setPaymentDueOn] = useState("");
  const [lines, setLines] = useState<LineDraft[]>([
    { productId: "", qty: "1", unitCost: "0" },
  ]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!companySlug) return;
    void Promise.all([
      listSuppliers(companySlug),
      listProducts(companySlug),
      getOrgSettings(companySlug),
    ]).then(([s, p, o]) => {
      setSuppliers(s.suppliers);
      setProducts(p.products.filter((x) => x.kind === "stocked"));
      setOrgTaxPriceMode(o.org.taxPriceMode);
      setOrgTaxRatePct(o.org.taxRatePct);
      if (s.suppliers[0]) setSupplierId(s.suppliers[0].id);
    });
  }, [companySlug]);

  if (!companySlug) return null;

  async function ensureSupplier(): Promise<string> {
    if (supplierId) return supplierId;
    if (!newSupplierName.trim()) {
      throw new Error("Pick or create a supplier");
    }
    const { supplier } = await createSupplier(companySlug!, {
      name: newSupplierName.trim(),
    });
    setSuppliers((prev) => [...prev, supplier]);
    setSupplierId(supplier.id);
    return supplier.id;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const sid = await ensureSupplier();
      const clean = lines.filter((l) => l.productId && Number(l.qty) > 0);
      if (clean.length === 0) throw new Error("Add at least one line");
      const { order } = await createPurchaseOrder(companySlug!, {
        supplierId: sid,
        lines: clean,
        ...(taxRatePct.trim() ? { taxRatePct: taxRatePct.trim() } : {}),
        ...(taxPriceMode ? { taxPriceMode } : {}),
        ...(docNumber.trim() ? { number: docNumber.trim() } : {}),
        ...(supplierRef.trim() ? { supplierRef: supplierRef.trim() } : {}),
        ...(expectedOn ? { expectedOn } : {}),
        ...(paymentDueOn ? { paymentDueOn } : {}),
        submit: true,
      });
      navigate(`/c/${companySlug}/purchasing/orders/${order.id}`, {
        replace: true,
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  return (
      <PageFrame>
      <PageHeader
        title="New purchase order"
        description="Receive stock, then pay the supplier"
      />
      <form className="mt-8 space-y-4" onSubmit={(e) => void onSubmit(e)}>
        <div>
          <Label htmlFor="po-supplier">Supplier</Label>
          <AppPicker
            id="po-supplier"
            className="mt-1"
            title="Supplier"
            description="Search suppliers"
            placeholder="Choose supplier"
            clearLabel="— Create new below —"
            value={supplierId || null}
            options={suppliers.map((s) => ({
              value: s.id,
              label: s.name,
              keywords: s.name,
            }))}
            onChange={(id) => setSupplierId(id ?? "")}
          />
        </div>
        {!supplierId && (
          <div>
            <Label htmlFor="po-new-supplier">New supplier name</Label>
            <Input
              id="po-new-supplier"
              className="mt-1"
              value={newSupplierName}
              onChange={(e) => setNewSupplierName(e.target.value)}
            />
          </div>
        )}

        <div className="space-y-3">
          <div className="text-xs font-semibold uppercase tracking-wider text-[var(--color-muted)]">
            Lines
          </div>
          {lines.map((line, i) => (
            <div key={i} className="grid grid-cols-3 gap-2">
              <AppPicker
                className="col-span-3"
                title="Product"
                description="Search products"
                placeholder="Product…"
                value={line.productId || null}
                options={products.map((p) => ({
                  value: p.id,
                  label: p.name,
                  keywords: `${p.name} ${p.sku ?? ""} ${p.barcode ?? ""}`,
                  description: p.sku ?? undefined,
                }))}
                onChange={(id) => {
                  const next = [...lines];
                  next[i] = { ...line, productId: id ?? "" };
                  setLines(next);
                }}
              />
              <Input
                className="font-mono"
                placeholder="Qty"
                value={line.qty}
                onChange={(e) => {
                  const next = [...lines];
                  next[i] = { ...line, qty: e.target.value };
                  setLines(next);
                }}
              />
              <Input
                className="col-span-2 font-mono text-right tabular-nums"
                inputMode="decimal"
                placeholder="Unit cost"
                value={line.unitCost}
                onChange={(e) => {
                  const next = [...lines];
                  next[i] = { ...line, unitCost: e.target.value };
                  setLines(next);
                }}
              />
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              setLines([...lines, { productId: "", qty: "1", unitCost: "0" }])
            }
          >
            Add line
          </Button>
        </div>

        <AdvancedSection subtitle="refs & dates">
          <DocNumberField
            companySlug={companySlug}
            docType="purchase_order"
            value={docNumber}
            onChange={setDocNumber}
          />
          <div>
            <Label htmlFor="po-supplier-ref">Their PO / ref</Label>
            <Input
              id="po-supplier-ref"
              className="mt-1"
              value={supplierRef}
              onChange={(e) => setSupplierRef(e.target.value)}
              maxLength={120}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="po-expected-on">Expected goods</Label>
              <Input
                id="po-expected-on"
                type="date"
                className="mt-1"
                value={expectedOn}
                onChange={(e) => setExpectedOn(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="po-payment-due">Payment due</Label>
              <Input
                id="po-payment-due"
                type="date"
                className="mt-1"
                value={paymentDueOn}
                onChange={(e) => setPaymentDueOn(e.target.value)}
              />
            </div>
          </div>
        </AdvancedSection>

        <TaxAdvancedFields
          orgTaxPriceMode={orgTaxPriceMode}
          orgTaxRatePct={orgTaxRatePct}
          taxPriceMode={taxPriceMode}
          taxRatePct={taxRatePct}
          onTaxPriceMode={setTaxPriceMode}
          onTaxRatePct={setTaxRatePct}
        />

        {error && <Alert variant="destructive">{error}</Alert>}
        <div className="flex gap-3">
          <Button type="submit" pending={busy} pendingLabel="Saving…">
            Submit PO
          </Button>
          <Button type="button" variant="outline" asChild>
            <Link
              to={`/c/${companySlug}/purchasing/orders`}
              className="no-underline"
            >
              Cancel
            </Link>
          </Button>
        </div>
      </form>
    </PageFrame>
  );
}
